/** The board's part-to-whole headline (L1): where every paid seat sits.
 *
 *  One donut, three segments — assigned & active, assigned to a reclaim
 *  candidate, and unused — with the paid-seat total in the hole, so the
 *  composition and the headline number read together. Segment colours carry
 *  status meaning (accent = working, danger = leaking on dead accounts,
 *  warning = idle inventory) and are never colour-alone: the legend, the
 *  tooltip and the table twin all name them.
 *
 *  **The whole is purchased capacity, and the three parts add up to it
 *  exactly.** Assignments beyond it are real but they are not seats the tenant
 *  owns — they stand on an expired subscription's grace period — so they sit
 *  outside the donut as a labelled footnote instead of quietly stretching the
 *  total. Folding them in is the arithmetic this board was built to stop.
 *
 *  Every segment is a door: reclaim candidates drill into the admin-gated
 *  account list; everything else opens the table twin with the exact counts.
 */

import { Chart, type ChartConfig } from "@norican/dataviz";
import { type DataGridColumnDef, EmptyState, Typography } from "@norican/react";

import { ChartCard, TABLE_TWIN_PARAM } from "@/components/board";
import type { LicenseTotals, ReclaimableSummary } from "@/lib/api";
import { useDrawerParam, useDrillNavigate } from "@/lib/drill";

import { formatPct, formatSeats } from "./licence-format";
import { RECLAIMABLE_ACCOUNTS_PATH } from "./reclaimable-summary";

/** `?data=seat-split` opens this chart's table twin. */
const TWIN_ID = "seat-split";

/** The backend rebuilds its snapshot every 15 minutes; flag anything older. */
const STALE_AFTER_MS = 15 * 60_000;

const ACTIVE = "Assigned & active";
const RECLAIMABLE = "Reclaim candidates";
const UNUSED = "Unused";

/** Status colours, not categorical ones: the three states of a paid seat. */
const SEAT_SPLIT_CONFIG: ChartConfig = {
  [ACTIVE]: { label: ACTIVE, color: "var(--norican-accent)" },
  [RECLAIMABLE]: { label: RECLAIMABLE, color: "var(--norican-danger)" },
  [UNUSED]: { label: UNUSED, color: "var(--norican-warning)" },
};

interface SeatSplitRow {
  key: string;
  /** Display label — also the `SEAT_SPLIT_CONFIG` key, per the kit's donut story. */
  name: string;
  seats: number;
  note: string;
}

interface SeatSplitDonutProps {
  totals: LicenseTotals;
  reclaimable: ReclaimableSummary;
  /** Whether the viewer holds the admin role — routes the reclaim drill. */
  isAdmin: boolean;
  generatedAt: string | null | undefined;
}

export function SeatSplitDonut({ totals, reclaimable, isAdmin, generatedAt }: SeatSplitDonutProps) {
  const twin = useDrawerParam(TABLE_TWIN_PARAM);
  const navigateDrill = useDrillNavigate();

  // Only the assignments that fit inside purchased capacity belong in a
  // part-to-whole of purchased capacity. The rest are the over-assigned seats,
  // reported below the chart in their own words.
  const assignedWithinCapacity = Math.max(totals.assigned_seats - totals.over_assigned_seats, 0);
  // Reclaim candidates hold assigned seats, so they are carved out of the
  // assigned figure — clamped so the active segment cannot go negative.
  const reclaimableSeats = Math.min(reclaimable.reclaimable_seats, assignedWithinCapacity);
  const rows: SeatSplitRow[] = [
    {
      key: "active",
      name: ACTIVE,
      seats: assignedWithinCapacity - reclaimableSeats,
      note: "Held by enabled, internal, recently active accounts",
    },
    {
      key: "reclaimable",
      name: RECLAIMABLE,
      seats: reclaimableSeats,
      note: reclaimable.sign_in_data_available
        ? "Assigned to disabled, guest, or 90d+ idle accounts"
        : "Assigned to disabled or guest accounts (idle accounts need AuditLog.Read.All)",
    },
    {
      key: "unused",
      name: UNUSED,
      seats: totals.unused_seats,
      note: "Paid for, assigned to nobody",
    },
  ];
  const segments = rows.filter((row) => row.seats > 0);

  const twinColumns: DataGridColumnDef<SeatSplitRow>[] = [
    {
      id: "segment",
      header: "Segment",
      accessor: (row) => row.name,
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-fg font-medium">{row.name}</span>
          <span className="text-fg-muted text-xs">{row.note}</span>
        </div>
      ),
    },
    {
      id: "seats",
      header: "Seats",
      numeric: true,
      accessor: (row) => row.seats,
      cell: (row) => <span className="text-fg tabular-nums">{formatSeats(row.seats)}</span>,
    },
    {
      id: "share",
      header: "Share of paid",
      numeric: true,
      accessor: (row) => row.seats,
      cell: (row) => (
        <span className="text-fg-muted tabular-nums">
          {totals.purchased_seats > 0 ? formatPct((row.seats / totals.purchased_seats) * 100) : "—"}
        </span>
      ),
    },
  ];

  return (
    <ChartCard
      title="Seat split"
      caption={
        reclaimable.sign_in_data_available
          ? `${totals.counted_skus} paid SKUs · expiring capacity excluded`
          : `${totals.counted_skus} paid SKUs · idle accounts not counted (no sign-in data)`
      }
      since={generatedAt}
      staleAfterMs={STALE_AFTER_MS}
      tableTwin={{
        id: TWIN_ID,
        ariaLabel: "Seat split by segment, in seats and share of paid seats",
        data: rows,
        columns: twinColumns,
        getRowId: (row) => row.key,
      }}
    >
      {segments.length === 0 ? (
        <EmptyState
          size="sm"
          title="No paid seats"
          description="No counted SKU carries any purchased seats."
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="relative">
            <Chart.Container
              config={SEAT_SPLIT_CONFIG}
              aria-label={`Seat split of ${formatSeats(totals.purchased_seats)} paid seats: assigned and active, reclaim candidates, unused`}
              style={{ height: 240 }}
            >
              <Chart.PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <Chart.Tooltip
                  content={
                    <Chart.TooltipContent
                      hideLabel
                      formatter={(value) =>
                        `${formatSeats(typeof value === "number" ? value : Number(value))} seats`
                      }
                    />
                  }
                />
                <Chart.Pie
                  {...Chart.seriesDefaults}
                  data={segments}
                  dataKey="seats"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="60%"
                  outerRadius="82%"
                  paddingAngle={2}
                  cornerRadius={3}
                  stroke="var(--norican-surface)"
                  strokeWidth={2}
                  onClick={(item: { name?: string | number }) => {
                    if (item.name === RECLAIMABLE && isAdmin) {
                      navigateDrill(RECLAIMABLE_ACCOUNTS_PATH);
                      return;
                    }
                    twin.open(TWIN_ID);
                  }}
                >
                  {segments.map((row) => (
                    <Chart.Cell key={row.key} fill={SEAT_SPLIT_CONFIG[row.name]?.color} />
                  ))}
                </Chart.Pie>
              </Chart.PieChart>
            </Chart.Container>
            {/* Center total, per the kit's Donut story: overlaid, never a slice. */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
            >
              <Typography variant="caption" className="font-semibold tracking-wide uppercase">
                Paid seats
              </Typography>
              <Typography variant="heading-2" as="span">
                {formatSeats(totals.purchased_seats)}
              </Typography>
            </div>
          </div>
          {/* Legend below the chart (kit Donut story pattern) so it does not
              push the donut — and its centred total — off-centre. */}
          <div className="text-fg-muted flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs">
            {rows.map((row) => (
              <span key={row.key} className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden
                  className="size-2.5 rounded-full"
                  style={{ background: SEAT_SPLIT_CONFIG[row.name]?.color }}
                />
                {row.name} · <span className="tabular-nums">{formatSeats(row.seats)}</span>
              </span>
            ))}
          </div>
          {totals.over_assigned_seats > 0 && (
            <p className="text-danger-fg text-center text-xs">
              Plus {formatSeats(totals.over_assigned_seats)} assignments beyond paid capacity,
              standing on {formatSeats(totals.grace_period_seats)} expiring seats. They are not in
              this chart because they are not seats the tenant owns.
            </p>
          )}
        </div>
      )}
    </ChartCard>
  );
}
